/* eslint-disable confluence-feature-gating/prefer-exp-val-equals */
import React, { useEffect, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { type ConversationAction, usePromptCards } from '@atlassian/rovo-conversation-actions';

import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	type RovoConfiguration,
	type RovoConfigurationProviderProps,
	isPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import { expVal } from '@confluence/feature-experiments';

import {
	useEditorRovoButtonActionsActions,
	useEditorRovoButtonActionsContext,
} from './store/EditorRovoButtonActionsStore.tsx';

import { getEmptyPageCreationActions } from './utils/getEmptyPageCreationActions.tsx';

import emptyPageGreetingIcon from './assets/empty-page-greeting-icon.svg';

const messages = defineMessages({
	emptyPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.empty-page-greeting-title',
		defaultMessage: 'Start your doc with a prompt',
		description: 'Greeting title shown in the Rovo empty state when the editor page is empty',
	},
});

const EditPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const viewPageRovoActions = useViewPageRovoActions();
	const editorActions = useEditorRovoButtonActionsActions();
	const editorActionsContext = useEditorRovoButtonActionsContext();
	const rovoConfigProviderProps = useRovoConfigProviderProps();
	const isEditorEmpty =
		isPageRovoConfigProviderProps(rovoConfigProviderProps) && rovoConfigProviderProps.isEditorEmpty;
	const { formatMessage } = useIntl();

	const { promptCards } = usePromptCards();

	const isEmptyPageCreationExperimentVariation =
		isEditorEmpty &&
		Array.isArray(promptCards) &&
		expVal('confluence_rovo_button_empty_page_creation_actions', 'cohort', 'not-enrolled') ===
			'variation';

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				if (isEmptyPageCreationExperimentVariation) {
					return Promise.resolve(getEmptyPageCreationActions(promptCards));
				}

				if (editorActionsContext?.reference) {
					return Promise.resolve(editorActions);
				}

				const viewPageActions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);

				return Promise.resolve([...editorActions, ...viewPageActions]);
			},
			customGreeting: isEmptyPageCreationExperimentVariation
				? {
						iconSrc: emptyPageGreetingIcon,
						title: formatMessage(messages.emptyPageGreetingTitle),
					}
				: undefined,
			shouldShowGreeting: isEmptyPageCreationExperimentVariation ? true : undefined,
			prioritizeCustomActionResults: isEmptyPageCreationExperimentVariation ? true : undefined,
		};
	}, [
		editorActions,
		editorActionsContext,
		viewPageRovoActions,
		isEmptyPageCreationExperimentVariation,
		promptCards,
		formatMessage,
	]);

	useEffect(() => {
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const EditPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps();
	return (
		<CommentCountsProvider contentId={contentId}>
			<EditPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
